import { Capacitor, CapacitorHttp } from '@capacitor/core';

export interface KeycloakEndpoints {
  authorization: string;
  token: string;
  endSession: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in: number;
  refresh_expires_in?: number;
}

export interface AuthorizeParams {
  clientId: string;
  redirectUri: string;
  scope: string;
  codeChallenge: string;
  state: string;
  nonce: string;
  /**
   * `none` asks Keycloak to answer from its existing SSO cookie without ever
   * rendering a form: it redirects straight back with a code, or with
   * `error=login_required` when there is no session to reuse.
   */
  prompt?: 'none';
}

export interface CallbackParams {
  code: string | null;
  state: string | null;
  error: string | null;
}

/**
 * Errors a `prompt=none` attempt returns when Keycloak has no reusable SSO
 * session. They are the expected negative answer, not a failure.
 */
const SILENT_SSO_DECLINED = new Set([
  'login_required',
  'interaction_required',
  'consent_required',
  'account_selection_required',
]);

export function isSilentSsoDeclined(error: string | null): boolean {
  return error !== null && SILENT_SSO_DECLINED.has(error);
}

const FORM_HEADERS = { 'Content-Type': 'application/x-www-form-urlencoded' };

/**
 * Thrown when the Keycloak token endpoint answers with a non-OK status.
 * Carries the HTTP status so callers can tell a definitive rejection
 * (4xx, e.g. `invalid_grant`) from a transient server failure (5xx).
 */
export class TokenRequestError extends Error {
  constructor(readonly status: number) {
    super(`Keycloak token request failed with status ${status}`);
    this.name = 'TokenRequestError';
  }
}

export function buildEndpoints(issuer: string): KeycloakEndpoints {
  const base = `${issuer.replace(/\/+$/, '')}/protocol/openid-connect`;
  return {
    authorization: `${base}/auth`,
    token: `${base}/token`,
    endSession: `${base}/logout`,
  };
}

export function buildAuthorizeUrl(
  endpoints: KeycloakEndpoints,
  params: AuthorizeParams
): string {
  const query = new URLSearchParams({
    client_id: params.clientId,
    redirect_uri: params.redirectUri,
    response_type: 'code',
    scope: params.scope,
    code_challenge: params.codeChallenge,
    code_challenge_method: 'S256',
    state: params.state,
    nonce: params.nonce,
  });
  if (params.prompt) {
    query.set('prompt', params.prompt);
  }
  return `${endpoints.authorization}?${query.toString()}`;
}

export function buildEndSessionUrl(
  endpoints: KeycloakEndpoints,
  clientId: string,
  postLogoutRedirectUri: string,
  idToken: string | null
): string {
  const query = new URLSearchParams({
    client_id: clientId,
    post_logout_redirect_uri: postLogoutRedirectUri,
  });
  if (idToken) {
    query.set('id_token_hint', idToken);
  }
  return `${endpoints.endSession}?${query.toString()}`;
}

export function parseCallback(callbackUrl: string): CallbackParams {
  const queryStart = callbackUrl.indexOf('?');
  const search = queryStart >= 0 ? callbackUrl.substring(queryStart + 1) : '';
  const params = new URLSearchParams(search);
  return {
    code: params.get('code'),
    state: params.get('state'),
    error: params.get('error'),
  };
}

export async function exchangeCode(
  endpoints: KeycloakEndpoints,
  clientId: string,
  code: string,
  redirectUri: string,
  codeVerifier: string
): Promise<TokenResponse> {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId,
    code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
  });
  return postToken(endpoints.token, body);
}

export async function refreshTokens(
  endpoints: KeycloakEndpoints,
  clientId: string,
  refreshToken: string
): Promise<TokenResponse> {
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: clientId,
    refresh_token: refreshToken,
  });
  return postToken(endpoints.token, body);
}

export function randomString(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

export async function sha256Challenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

export function decodeJwt(token: string): Record<string, unknown> {
  const segments = token.split('.');
  if (segments.length < 2) {
    return {};
  }
  const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );
  return JSON.parse(json) as Record<string, unknown>;
}

async function postToken(
  tokenUrl: string,
  body: URLSearchParams
): Promise<TokenResponse> {
  if (Capacitor.isNativePlatform()) {
    return postTokenNatively(tokenUrl, body);
  }
  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: FORM_HEADERS,
    body: body.toString(),
  });
  if (!response.ok) {
    throw new TokenRequestError(response.status);
  }
  return (await response.json()) as TokenResponse;
}

/**
 * A native build serves from `capacitor://localhost` (iOS) / `http://localhost`
 * (Android), so a `fetch` to the token endpoint is a cross-origin request that
 * Keycloak only answers with CORS headers when that exact origin is listed in the
 * client's Web Origins. Going through CapacitorHttp makes the call from the native
 * layer instead, where CORS does not apply — the app therefore needs no web origin
 * of its own on the Keycloak client.
 */
async function postTokenNatively(
  tokenUrl: string,
  body: URLSearchParams
): Promise<TokenResponse> {
  const response = await CapacitorHttp.post({
    url: tokenUrl,
    headers: FORM_HEADERS,
    data: body.toString(),
  });
  if (response.status < 200 || response.status > 299) {
    throw new TokenRequestError(response.status);
  }
  return (typeof response.data === 'string'
    ? JSON.parse(response.data)
    : response.data) as TokenResponse;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}
